import { blankAircraft, hydrateSceneryAirport, lookupAirport, resolveCoords } from "./airports";
import {
  applyContentXmlScheduleToScenery,
  contentXmlScheduleBlocked,
  inventoryIcaosFromPrefers,
} from "./contentXmlSchedule";
import { resolveAirportRunway } from "./airportRunways";
import { minStageNmFor, remapUnseasonalType } from "./charterRealism";
import { fuelDensityForSpec } from "./aircraftDatabase";
import { CHARTER_RANGE_FACTOR, planningCruiseKt } from "./hawker800xp";
import { halifaxMonth, halifaxYear, prefersType, seasonCaption, typeWeights } from "./season";
import { suggestDeparture } from "./clock";
import { haversineNm, initialTrueTrack } from "./geo";
import { ratedFleet } from "./profile";
import { fillScenario } from "./scenario";
import { withLegs } from "./schedule";
import { radioIdFor, type RadioCompany } from "./radio";
import type { AircraftId, AircraftSpec, DraftMission, FlightLeg, FlightType, SceneryAirport } from "./types";

export const FLIGHT_TYPE_LABEL: Record<FlightType, string> = {
  golf: "Golf inbound",
  sun: "Sun escape",
  vip: "VIP / leisure",
  corporate: "Corporate",
  medical: "Medevac",
  ferry: "Ferry / positioning",
};

export type GenerateInput = {
  scenery: SceneryAirport[];
  originIcao: string;
  flightType?: FlightType | "any";
  aircraftId?: AircraftId | "any";
  fleet?: AircraftSpec[];
  ratedIcaoTypes?: string[];
  now?: Date;
  preferIcaos?: string[];
  sceneryGateEnabled?: boolean;
  allowUninstalled?: boolean;
  scheduleOnlyContentXmlAirports?: boolean;
  company?: RadioCompany | null;
};

export type GenerateResult = { ok: true; mission: DraftMission } | { ok: false; error: string };

export function validForAircraft(
  spec: AircraftSpec,
  destRunwayFt: number,
  distanceNm: number,
  ferry = false,
): boolean {
  const limit = ferry ? spec.ferryRangeNm : spec.rangeNm;
  const minNm = minStageNmFor(spec, ferry);
  return distanceNm > minNm && distanceNm <= limit * CHARTER_RANGE_FACTOR;
}

export function eteMinutes(distanceNm: number, cruiseKt: number): number {
  const block = (distanceNm / Math.max(120, cruiseKt)) * 60;
  return Math.max(25, Math.round(block + 12));
}

export function fuelPlan(spec: AircraftSpec, eteMin: number): { fuelGal: number; fuelLbs: number } {
  const hours = eteMin / 60;
  const reserveHours = spec.reserveMin / 60;
  const density = fuelDensityForSpec(spec);
  const burnLb = spec.fuelBurnLbHr;
  if (typeof burnLb === "number" && Number.isFinite(burnLb) && burnLb > 0) {
    const fuelLbs = Math.round(spec.taxiFuelGal * density + burnLb * (hours + reserveHours));
    const fuelGal = density > 0 ? Math.round(fuelLbs / density) : 0;
    return { fuelGal, fuelLbs };
  }
  const gal = spec.taxiFuelGal + spec.fuelBurnGph * (hours + reserveHours);
  const fuelGal = Math.round(gal);
  return { fuelGal, fuelLbs: Math.round(fuelGal * density) };
}

export function fakeMetar(icao: string, now = new Date()): string {
  const d = now.getUTCDate().toString().padStart(2, "0");
  const h = now.getUTCHours().toString().padStart(2, "0");
  const m = (Math.floor(now.getUTCMinutes() / 5) * 5).toString().padStart(2, "0");
  const wind = 240 + Math.floor(Math.random() * 40);
  const kts = 4 + Math.floor(Math.random() * 12);
  const vis = Math.random() > 0.15 ? "9999" : "6000";
  const cloud = Math.random() > 0.4 ? "FEW040" : "BKN012";
  const temp = 6 + Math.floor(Math.random() * 16);
  const dew = temp - 3 - Math.floor(Math.random() * 5);
  const alt = 2980 + Math.floor(Math.random() * 40);
  const t = (n: number) => (n < 0 ? `M${String(Math.abs(n)).padStart(2, "0")}` : String(n).padStart(2, "0"));
  return `${icao} ${d}${h}${m}Z ${wind.toString().padStart(3, "0")}${kts.toString().padStart(2, "0")}KT ${vis} ${cloud} ${t(temp)}/${t(dew)} A${alt} NOSIG`;
}

export function computeLeg(
  seq: number,
  origin: SceneryAirport,
  dest: SceneryAirport,
  spec: AircraftSpec,
  departure: Date,
  paxCount: number,
  paxNames: string[],
  notes = "",
): FlightLeg | null {
  const o = resolveCoords(origin.icao, [origin]);
  const d = resolveCoords(dest.icao, [dest]);
  if (!o || !d) return null;
  const distanceNm = Math.round(haversineNm(o.lat, o.lon, d.lat, d.lon));
  const eteMin = eteMinutes(distanceNm, planningCruiseKt(spec, distanceNm));
  const fuel = fuelPlan(spec, eteMin);
  return {
    seq,
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    originCity: origin.city,
    destCity: dest.city,
    distanceNm,
    eteMin,
    fuelGal: fuel.fuelGal,
    fuelLbs: fuel.fuelLbs,
    trackTrue: Math.round(initialTrueTrack(o.lat, o.lon, d.lat, d.lon)),
    departureIso: departure.toISOString(),
    originMetar: fakeMetar(origin.icao, departure),
    destMetar: fakeMetar(dest.icao, departure),
    originRunwayFt: origin.runwayFt,
    destRunwayFt: dest.runwayFt,
    paxCount,
    paxNames,
    notes,
  };
}

function asScenery(icao: string, scenery: SceneryAirport[]): SceneryAirport | undefined {
  return hydrateSceneryAirport(icao, scenery);
}

function pickWeighted(available: FlightType[], month: number): FlightType {
  const w = typeWeights(month);
  const rows = available.map((item) => ({ item, weight: Math.max(0, w[item]) }));
  const total = rows.reduce((s, r) => s + r.weight, 0);
  if (total <= 0) return available[0] ?? "corporate";
  let roll = Math.random() * total;
  for (const row of rows) {
    roll -= row.weight;
    if (roll <= 0) return row.item;
  }
  return rows[rows.length - 1]?.item ?? "corporate";
}

function callsignFor(spec: AircraftSpec, company?: RadioCompany | null): string {
  return radioIdFor(spec, company);
}

export function generateMission(input: GenerateInput): GenerateResult {
  const inventory = inventoryIcaosFromPrefers({ preferIcaos: input.preferIcaos });
  const prepared = applyContentXmlScheduleToScenery(
    input.scenery ?? [],
    Boolean(input.scheduleOnlyContentXmlAirports),
    inventory,
  );
  if (!prepared.ok) return { ok: false, error: prepared.error };
  const scenery = prepared.scenery;
  if (scenery.length < 2) {
    return { ok: false, error: "Need at least two destinations to build a ticket." };
  }

  const originBlocked = contentXmlScheduleBlocked(
    Boolean(input.scheduleOnlyContentXmlAirports),
    inventory,
    [input.originIcao],
  );
  if (originBlocked) return { ok: false, error: originBlocked };

  const origin = asScenery(input.originIcao, scenery);
  if (!origin) {
    return { ok: false, error: `Origin ${input.originIcao} is not on file.` };
  }

  const now = input.now ?? new Date();
  const month = halifaxMonth(now);
  const year = halifaxYear(now);

  const catalog = input.fleet ?? [];
  const available = catalog.filter((a) => a.available && (a.status ?? "available") === "available");
  const preferred = ratedFleet(available, input.ratedIcaoTypes);
  const fleet =
    !input.aircraftId || input.aircraftId === "any"
      ? preferred.length
        ? preferred
        : available
      : catalog.filter((a) => a.id === input.aircraftId);
  if (!catalog.length) {
    return { ok: false, error: "No fleet on the book. Add a tail before generating a ticket." };
  }
  if (!fleet.length) {
    return { ok: false, error: "No matching aircraft available." };
  }

  const requestedType = input.flightType && input.flightType !== "any" ? input.flightType : undefined;
  const types: FlightType[] = requestedType
    ? [requestedType]
    : ["golf", "sun", "corporate", "vip", "medical"];

  let chosen: { spec: AircraftSpec; dest: SceneryAirport; type: FlightType } | null = null;

  const shuffledFleet = [...fleet].sort(() => Math.random() - 0.5);
  for (const spec of shuffledFleet) {
    const type = requestedType ?? pickWeighted(types, month);
    const candidates = scenery
      .filter((s) => s.icao.toUpperCase() !== origin.icao.toUpperCase())
      .map((s) => asScenery(s.icao, scenery))
      .filter((s): s is SceneryAirport => Boolean(s));

    const prefer = new Set((input.preferIcaos ?? []).map((c) => c.toUpperCase()));
    const gateOn = Boolean(input.sceneryGateEnabled && prefer.size);
    const allowUninstalled = input.allowUninstalled !== false;
    const scored = candidates
      .map((dest) => {
        const oc = resolveCoords(origin.icao, [origin]);
        const dc = resolveCoords(dest.icao, [dest]);
        if (!oc || !dc) return null;
        const nm = haversineNm(oc.lat, oc.lon, dc.lat, dc.lon);
        const ferry = type === "ferry";
        if (!validForAircraft(spec, dest.runwayFt, nm, ferry)) return null;
        const installed = prefer.has(dest.icao.toUpperCase());
        if (gateOn && !allowUninstalled && !installed) return null;
        return {
          dest,
          preferred: prefersType(type, dest.icao),
          installed,
        };
      })
      .filter((row): row is { dest: SceneryAirport; preferred: boolean; installed: boolean } => Boolean(row));

    const installedPool = gateOn ? scored.filter((s) => s.installed) : [];
    const typePool = scored.filter((s) => s.preferred);
    const pool = installedPool.length ? installedPool : typePool.length ? typePool : scored;
    const use = [...pool].sort((a, b) => {
      if (gateOn && a.installed !== b.installed) return a.installed ? -1 : 1;
      return Math.random() - 0.5;
    });
    const hit = use[0];
    if (hit) {
      chosen = { spec, dest: hit.dest, type: remapUnseasonalType(type, origin.icao, hit.dest.icao, month) };
      break;
    }
  }

  if (!chosen) {
    return {
      ok: false,
      error: "No legal destination in the destinations list for the selected aircraft / type (range or runway).",
    };
  }

  const destRec = lookupAirport(chosen.dest.icao);
  const isMx = Boolean(destRec?.serviceCenter) || chosen.type === "ferry";
  const serviceLabel = isMx ? destRec?.serviceLabel : undefined;
  const story = fillScenario(chosen.type, origin.name, chosen.dest.name, chosen.spec, serviceLabel);
  const departure = suggestDeparture(now);
  const leg = computeLeg(
    1,
    origin,
    chosen.dest,
    chosen.spec,
    departure,
    story.paxCount,
    story.paxNames,
    story.purpose,
  );
  if (!leg) {
    return { ok: false, error: "Could not compute the leg — missing coordinates." };
  }

  const draft: DraftMission = withLegs({
    id: `ns-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`,
    createdAt: now.toISOString(),
    callsign: callsignFor(chosen.spec, input.company),
    aircraftId: chosen.spec.id,
    aircraftName: chosen.spec.name,
    registration: chosen.spec.registration,
    flightType: chosen.type,
    originIcao: origin.icao,
    destIcao: chosen.dest.icao,
    originName: origin.name,
    destName: chosen.dest.name,
    originCity: origin.city,
    destCity: chosen.dest.city,
    distanceNm: leg.distanceNm,
    eteMin: leg.eteMin,
    fuelGal: leg.fuelGal,
    fuelLbs: leg.fuelLbs,
    trackTrue: leg.trackTrue,
    paxCount: story.paxCount,
    paxNames: story.paxNames,
    scenario: story.scenario,
    purpose: story.purpose,
    departureIso: departure.toISOString(),
    weatherNote: seasonCaption(month, year),
    originMetar: leg.originMetar,
    destMetar: leg.destMetar,
    isMaintenance: story.isMaintenance,
    serviceLabel: story.serviceLabel,
    originRunwayFt: origin.runwayFt,
    destRunwayFt: chosen.dest.runwayFt,
    legs: [leg],
    source: "ai",
    seasonNote: seasonCaption(month, year),
  });

  return { ok: true, mission: draft };
}

export function applyAircraftToDraft(
  draft: DraftMission,
  spec: AircraftSpec,
  scenery: SceneryAirport[],
): DraftMission {
  const origin = asScenery(draft.originIcao, scenery);
  const dest = asScenery(draft.destIcao, scenery);
  if (!origin || !dest) {
    return {
      ...draft,
      aircraftId: spec.id,
      aircraftName: spec.name,
      registration: spec.registration,
    };
  }
  const paxCount = Math.min(draft.paxCount, spec.maxPax);
  const paxNames = draft.paxNames.slice(0, paxCount);
  const departure = new Date(draft.departureIso);
  const computed = computeLeg(1, origin, dest, spec, departure, paxCount, paxNames, draft.purpose);
  if (!computed) {
    return {
      ...draft,
      aircraftId: spec.id,
      aircraftName: spec.name,
      registration: spec.registration,
      paxCount,
      paxNames,
    };
  }
  return withLegs({
    ...draft,
    callsign: radioIdFor(spec),
    aircraftId: spec.id,
    aircraftName: spec.name,
    registration: spec.registration,
    paxCount,
    paxNames,
    distanceNm: computed.distanceNm,
    eteMin: computed.eteMin,
    fuelGal: computed.fuelGal,
    fuelLbs: computed.fuelLbs,
    trackTrue: computed.trackTrue,
    originMetar: computed.originMetar,
    destMetar: computed.destMetar,
    originRunwayFt: origin.runwayFt,
    destRunwayFt: dest.runwayFt,
    legs: [computed],
  });
}

export function emptyManualDraft(
  originIcao = "CYPD",
  destIcao = "CYHZ",
  aircraftId?: AircraftId,
  fleet: AircraftSpec[] = [],
): DraftMission {
  const hit = aircraftId ? fleet.find((a) => a.id === aircraftId) : undefined;
  const spec = hit ?? fleet[0] ?? blankAircraft();
  const origin = asScenery(originIcao, []) ?? {
    id: originIcao,
    icao: originIcao,
    name: originIcao,
    city: originIcao,
    country: "—",
    runwayFt: resolveAirportRunway(originIcao).longestFt,
    notes: "",
  };
  const dest = asScenery(destIcao, []) ?? {
    id: destIcao,
    icao: destIcao,
    name: destIcao,
    city: destIcao,
    country: "—",
    runwayFt: resolveAirportRunway(destIcao).longestFt,
    notes: "",
  };
  const now = new Date();
  const departure = suggestDeparture(now);
  const story = fillScenario("corporate", origin.name, dest.name, spec);
  const computed = computeLeg(1, origin, dest, spec, departure, story.paxCount, story.paxNames);
  const leg: FlightLeg = computed ?? {
    seq: 1,
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    originCity: origin.city,
    destCity: dest.city,
    distanceNm: 0,
    eteMin: 60,
    fuelGal: 0,
    fuelLbs: 0,
    trackTrue: 0,
    departureIso: departure.toISOString(),
    originMetar: fakeMetar(origin.icao, now),
    destMetar: fakeMetar(dest.icao, now),
    originRunwayFt: origin.runwayFt,
    destRunwayFt: dest.runwayFt,
    paxCount: story.paxCount,
    paxNames: story.paxNames,
    notes: "",
  };

  return withLegs({
    id: `manual-${now.getTime().toString(36)}`,
    createdAt: now.toISOString(),
    callsign: callsignFor(spec),
    aircraftId: spec.id,
    aircraftName: spec.name,
    registration: spec.registration,
    flightType: "corporate",
    originIcao: origin.icao,
    destIcao: dest.icao,
    originName: origin.name,
    destName: dest.name,
    originCity: origin.city,
    destCity: dest.city,
    distanceNm: leg.distanceNm,
    eteMin: leg.eteMin,
    fuelGal: leg.fuelGal,
    fuelLbs: leg.fuelLbs,
    trackTrue: leg.trackTrue,
    paxCount: story.paxCount,
    paxNames: story.paxNames,
    scenario: story.scenario,
    purpose: "Manual ticket",
    departureIso: departure.toISOString(),
    weatherNote: "",
    originMetar: leg.originMetar,
    destMetar: leg.destMetar,
    isMaintenance: false,
    originRunwayFt: origin.runwayFt,
    destRunwayFt: dest.runwayFt,
    legs: [leg],
    source: "manual",
  });
}
